import { AdmissionStatusEnum, TripTypeEnum } from "../../Orchestrator/Types/Enums/globalDefaultEnum";

/**
 * Resolving "what does each passenger hold on this direction" while an exchange
 * is in flight.
 *
 * A direction normally resolves to one train trip, so every passenger-list
 * builder in the codebase took `trips.find(direction)` and read that trip's
 * offers. Mid-exchange the direction holds TWO trips — the original (admissions
 * flipped to EXCHANGE_ONGOING, being exchanged away) and the replacement
 * (PREBOOKED) — and the API lists the original first. First-match therefore
 * priced the passenger off the ticket they are surrendering and stamped
 * `EXCHANGE_ONGOING`, a status the downstream fulfilment filters (FULFILLED /
 * PREBOOKED / EXCHANGED / ON_HOLD) reject. The passenger dropped out of
 * `successPassengers` and the change-date total collapsed to AED 0 — a free
 * upgrade, then a 400 invalid-operation from /api/payment/session on the 0.00
 * amount (reproduced live on int for a Comfort → Premium change-date).
 *
 * These two helpers are the shared fix. Keep them together: picking the offers
 * without dropping the superseded admissions (or vice versa) reintroduces the
 * defect.
 */

type AdmissionLike = { status?: string; passengerIds?: string[] };
type OfferLike = { tripCoverage?: { coveredTripId?: string }; admissions?: AdmissionLike[] };
type TripLike = { id: string; direction?: string };

/**
 * The offers describing a direction's CURRENT state.
 *
 * No exchange in flight — the overwhelmingly common case — returns exactly the
 * first trip's offers, i.e. the historical behaviour, so unrelated flows
 * (search, book-it-again, booking details, on-hold) are untouched. Only when
 * the direction actually holds an EXCHANGE_ONGOING admission does it widen to
 * every trip in that direction, so the replacement is visible alongside the
 * passengers who are staying put. Widening unconditionally would change prices
 * on ordinary multi-trip directions (an onward-shuttle trip shares the train
 * trip's direction), which is why it is gated.
 */
export function getOffersForDirection<O extends OfferLike, T extends TripLike>(
    trips: T[] | null | undefined,
    offers: O[] | null | undefined,
    direction: TripTypeEnum,
): O[] {
    const allOffers = offers || [];
    const tripsInDirection = (trips || []).filter(trip => trip?.direction === direction);
    const offersForTrip = (tripId: string) =>
        allOffers.filter(offer => offer?.tripCoverage?.coveredTripId === tripId);

    const hasExchangeInFlight = tripsInDirection.some(trip =>
        offersForTrip(trip.id).some(offer =>
            (offer?.admissions || []).some(add => add?.status === AdmissionStatusEnum.EXCHANGE_ONGOING),
        ),
    );

    if (!hasExchangeInFlight) {
        const firstTrip = tripsInDirection[0];
        return firstTrip ? offersForTrip(firstTrip.id) : [];
    }

    return tripsInDirection.flatMap(trip => offersForTrip(trip.id));
}

/**
 * Of one passenger's admissions on a direction, the ones that still represent
 * what they hold: an EXCHANGE_ONGOING admission is superseded as soon as the
 * same passenger has a live admission alongside it, so it must not be priced or
 * used for their status.
 *
 * When EVERY admission is EXCHANGE_ONGOING there is no replacement yet (the
 * exchange was started but no new offer selected), so they are returned as-is
 * rather than emptying the passenger out.
 */
export function dropSupersededAdmissions<A extends AdmissionLike>(admissions: A[]): A[] {
    const live = (admissions || []).filter(add => add?.status !== AdmissionStatusEnum.EXCHANGE_ONGOING);
    return live.length > 0 ? live : (admissions || []);
}
